import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface Coordinate {
  label: string;
  value: string;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

// Köln coordinates as a personal touch
const COORDINATES: Coordinate[] = [
  { label: 'LAT', value: '50.9375°N', position: 'top-left' },
  { label: 'SYSTEM', value: 'v2.0.25', position: 'top-right' },
  { label: 'STATUS', value: 'ACTIVE', position: 'bottom-left' },
  { label: 'LNG', value: '6.9603°E', position: 'bottom-right' },
];

const positionClasses: Record<Coordinate['position'], string> = {
  'top-left': 'top-6 left-6',
  'top-right': 'top-6 right-6 text-right',
  'bottom-left': 'bottom-6 left-6',
  'bottom-right': 'bottom-6 right-6 text-right',
};

interface CoordinateItemProps {
  coordinate: Coordinate;
  delay: number;
}

function CoordinateItem({ coordinate, delay }: CoordinateItemProps) {
  const [displayText, setDisplayText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chars = coordinate.value.split('');
    let currentIndex = 0;

    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        if (currentIndex <= chars.length) {
          setDisplayText(chars.slice(0, currentIndex).join(''));
          currentIndex++;
        } else {
          clearInterval(interval);
        }
      }, 50); // Typewriter speed

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timer);
  }, [coordinate.value, delay]);

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(
        containerRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, delay: delay / 1000, ease: 'power2.out' }
      );
    }
  }, [delay]);

  return (
    <div
      ref={containerRef}
      className={`absolute ${positionClasses[coordinate.position]} font-mono text-xs opacity-0`}
    >
      <div className="text-white/20 text-[10px] mb-0.5">{coordinate.label}</div>
      <div className="text-white/40 tracking-wider">
        {displayText}
        <span className="animate-pulse">_</span>
      </div>
    </div>
  );
}

interface CoordinateDisplayProps {
  startDelay?: number;
}

export function CoordinateDisplay({ startDelay = 200 }: CoordinateDisplayProps) {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {COORDINATES.map((coord, index) => (
        <CoordinateItem
          key={coord.position}
          coordinate={coord}
          delay={startDelay + index * 100}
        />
      ))}
    </div>
  );
}
